import React, { useState, useEffect } from 'react';
import { Stack, Button, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ChevronRightDarkIcon from 'assets/image/icons/chevron_right_dark.svg';
import RefreshIcon from 'assets/image/icons/refresh_black.svg';
import EyeIcon from 'assets/image/icons/eye.svg';
import EyeSlashIcon from 'assets/image/icons/eye_slash.svg';
import { openWindow } from 'lib';
import { ONLINE_CHARGE_TRANSITION_PAGE_URL } from 'lib/constants';
import PrepaidIcon from 'assets/image/icons/prepaid.svg';
import usePrepaidBalanceTotal from 'lib/hooks/usePrepaidBalanceTotal';
import {
  STORAGE_KEYS,
  getStorageBoolean,
  setStorageBoolean
} from 'lib/storageUtils';

/**
 * 現在時刻の更新日時文字列を返す
 */
const getUpdateTimeText = () => {
  const now = new Date();
  // yyyy/MM/dd HH:mm:ss 形式でフォーマット
  const formatted = now
    .toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })
    .replace(/\//g, '/');

  return `${formatted} 時点`;
};

const CardPrepaidBalance: React.FC = () => {
  // ローカルストレージから表示/非表示状態を取得（デフォルトはtrue=表示）
  const [isBalanceVisible, setIsBalanceVisible] = useState<boolean>(true);
  const [updateTime, setUpdateTime] = useState<string>(getUpdateTimeText());

  // 初期表示時にローカルストレージから表示/非表示設定を読み込む
  useEffect(() => {
    setIsBalanceVisible(
      getStorageBoolean(STORAGE_KEYS.PREPAID_BALANCE_VISIBLE)
    );
  }, []);

  // プリペイド残高データの取得
  const { prepaidBalanceTotal, isLoadingGetPrepaid, executeCurrentPrepaid } =
    usePrepaidBalanceTotal(true);

  const handleUpdateTime = () => {
    const newTime = getUpdateTimeText();
    setUpdateTime(newTime);
    executeCurrentPrepaid();
  };

  // 表示/非表示を切り替え、ローカルストレージに保存
  const toggleBalanceVisibility = () => {
    const newVisibility = !isBalanceVisible;
    setIsBalanceVisible(newVisibility);
    setStorageBoolean(STORAGE_KEYS.PREPAID_BALANCE_VISIBLE, newVisibility);
  };

  return (
    <Stack
      gap={4}
      className="px-3 py-4 px-4 py-3 mb-3 section-cont section-cont--rounded-2 section-cont--shadow justify-content-between"
    >
      <Stack direction="horizontal" className="w-100 justify-content-between">
        <span className="barcodeValidate">{updateTime}</span>
        <Stack direction="horizontal" gap={3}>
          <Button
            className="view-btn btn--secondary-light rounded-circle p-2"
            onClick={toggleBalanceVisibility}
          >
            <img
              src={isBalanceVisible ? EyeIcon : EyeSlashIcon}
              alt="表示切替アイコン"
              width={20}
              height={20}
            />
          </Button>
          <Button
            className="refresh-btn btn--secondary-light text--dark"
            onClick={handleUpdateTime}
          >
            <img className="me-1" src={RefreshIcon} alt="更新アイコン" />
            更新
          </Button>
        </Stack>
      </Stack>

      <Stack direction="horizontal" className="w-100 justify-content-between">
        <span className="d-flex align-items-center">
          <img
            src={PrepaidIcon}
            alt="プリカアイコン"
            style={{ width: 36, height: 36 }}
            className="me-2"
          />
          <span className="d-flex flex-column fw-bold">
            <span>プリカ</span>
            <span>残高</span>
          </span>
        </span>
        <span>
          <span className="fw-bold text--big me-2">
            {isLoadingGetPrepaid ? (
              <Spinner animation="border" role="status" />
            ) : isBalanceVisible ? (
              prepaidBalanceTotal?.toLocaleString() ?? '---'
            ) : (
              '--,---'
            )}
          </span>
          <span className="fw-normal fs-3">円</span>
        </span>
      </Stack>

      <Link to="/history" className="btn btn--secondary-outline py-3">
        残高内訳・履歴・有効期限{' '}
        <img src={ChevronRightDarkIcon} className="ms-2" alt="右矢印アイコン" />
      </Link>

      <Button
        className="btn btn--secondary-outline py-3 w-100"
        onClick={() => {
          openWindow(ONLINE_CHARGE_TRANSITION_PAGE_URL);
        }}
      >
        クレジットカードチャージ
        <img src={ChevronRightDarkIcon} className="ms-2" alt="右矢印アイコン" />
      </Button>
    </Stack>
  );
};

export default CardPrepaidBalance;
